'use client';

import { useTranslations } from 'next-intl';
import React, { type ReactNode, useCallback, useRef, useState } from 'react';
import { useRegisterStickyElement } from '../../../../utils/src/providers/ScrollPaddingTopProvider';
import { PageHeader } from '../../PageHeader/PageHeader';
import { MonthsChipsSelector } from '../Wallet/Transactions/components/MonthsChipsSelector';
import { Transactions } from '../Wallet/Transactions/Transactions';
import { TransactionsProvider, useTransactionsContext } from '../Wallet/Transactions/TransactionsContext';
import type { MonthKey } from '../Wallet/Transactions/types';

const NAMESPACE = 'my_account.wallet.transactions_page';

function TransactionsPageContent() {
  return (
    <TransactionsProvider namespace={NAMESPACE}>
      <TransactionsPageContentInner />
    </TransactionsProvider>
  );
}

function TransactionsPageContentInner() {
  const t = useTranslations(NAMESPACE);
  const { currentMonthKey, loadMonth, setScrollTarget, acquireScrollLock, isScrollLocked } = useTransactionsContext();

  const [selectedMonth, setSelectedMonth] = useState<MonthKey>(currentMonthKey);

  const onMonthSelect = useCallback(
    (monthKey: MonthKey) => {
      setSelectedMonth(monthKey);
      loadMonth(monthKey);
      setScrollTarget(monthKey);
      acquireScrollLock(monthKey);
    },
    [loadMonth, setScrollTarget, acquireScrollLock]
  );

  const onVisibleMonthChange = useCallback(
    (monthKey: MonthKey) => {
      if (isScrollLocked()) return;
      setSelectedMonth(monthKey);
    },
    [isScrollLocked]
  );

  const onScrollToOlderTransactions = useCallback(() => setScrollTarget('older'), [setScrollTarget]);

  return (
    <div className="min-h-screen md:bg-background-secondary" data-testid="transactions-page" data-track-feature="My Account" data-track-container="Wallet Transactions">
      <PageHeaderSection title={t('page.title')} />

      <MonthsSelectorSection>
        <MonthsChipsSelector selectedMonth={selectedMonth} onMonthSelect={onMonthSelect} onScrollToOlderTransactions={onScrollToOlderTransactions} />
      </MonthsSelectorSection>

      <TransactionsSection>
        <Transactions onVisibleMonthChange={onVisibleMonthChange} />
      </TransactionsSection>
    </div>
  );
}

// Sub-components for better separation of concerns
const PageHeaderSection = React.memo(function PageHeaderSection({ title }: { title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useRegisterStickyElement(ref, { id: 'transactions-page-header' });
  return (
    <div className="sticky z-10 bg-background top-(--sticky-primary-header-height,0px)" ref={ref}>
      <div className="ll-grid-container">
        <PageHeader className="ll-container-inner py-6" title={title} variant="subheader" />
      </div>
    </div>
  );
});

const MonthsSelectorSection = React.memo(function MonthsSelectorSection({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useRegisterStickyElement(ref);

  return (
    <div
      className="sticky z-10 bg-background border-b border-container top-[calc(var(--sticky-primary-header-height,0px)+var(--sticky-transactions-page-header-height,0px))]"
      ref={ref}
    >
      <div className="ll-grid-container">
        <div className="ll-container-inner-full py-6 pt-3">{children}</div>
      </div>
    </div>
  );
});

const TransactionsSection = React.memo(function TransactionsSection({ children }: { children: ReactNode }) {
  return (
    <div className="md:p-8">
      <div className="max-w-xl mx-auto md:bg-background md:rounded-lg md:shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
});

export default TransactionsPageContent;
